import { useEffect, useState } from 'react';
import { Save, RefreshCw, Clock, Plus, Trash2, MessageSquareQuote, FileEdit, ShoppingCart } from 'lucide-react';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ui/ToastSystem';
import { SERVICES as CATALOG_SERVICES } from '../../data/services';
import { clearPlanAccessMatrixCache } from '../../lib/planAccessMatrix';
import { clearTestimonialsCache, type Testimonial } from '../../hooks/useServiceTestimonials';
import { clearServiceCatalogOverridesCache, type ServiceCatalogOverrides, type ServiceCatalogOverride } from '../../hooks/useServiceCatalogOverrides';

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

interface AvulsoProduct {
  id: string;
  service_slug?: string;
  name: string;
  description?: string;
  price_brl: number;
  price_usd?: number;
  enabled: boolean;
}

interface ServiceAccess {
  enabled:          boolean;
  quota_per_month:  number | null; // null = unlimited
  quota_per_year:   number | null; // null = unlimited; only shown for annual tier
}

type PlanTier = 'free' | 'monthly' | 'annual';

type AccessMatrix = Record<string, Record<PlanTier, ServiceAccess>>;

// ─────────────────────────────────────────
// Constants
// ─────────────────────────────────────────

const SERVICES: { id: string; name: string; icon: string; category: string }[] = [
  // Página Inicial
  { id: 'sign-forecast',    name: 'Previsão do Signo',            icon: '🌟', category: 'Página Inicial' },
  { id: 'sign-synastry',    name: 'Sinastria entre Signos',       icon: '♾️', category: 'Página Inicial' },
  // Meu Mapa
  { id: 'birth-chart',      name: 'Mapa Astral',                  icon: '🌌', category: 'Meu Mapa' },
  { id: 'forecast-daily',   name: 'Previsão Diária',              icon: '🔭', category: 'Meu Mapa' },
  { id: 'forecast-weekly',  name: 'Previsão Semanal',             icon: '🔭', category: 'Meu Mapa' },
  { id: 'forecast-monthly', name: 'Previsão Mensal',              icon: '🔭', category: 'Meu Mapa' },
  { id: 'forecast-annual',  name: 'Previsão Anual',               icon: '🔭', category: 'Meu Mapa' },
  // Relacionamentos
  { id: 'synastry',         name: 'Sinastria',                    icon: '💫', category: 'Relacionamentos' },
  { id: 'astromatch',       name: 'AstroMatch',                   icon: '💘', category: 'Relacionamentos' },
  // Descobertas
  { id: 'astrocartography', name: 'Astrocartografia',             icon: '🌍', category: 'Descobertas' },
  { id: 'questions',        name: 'Perguntar aos Astros',         icon: '🔮', category: 'Descobertas' },
  // Tarot de Marselha
  { id: 'tarot-single',     name: 'Tarot de Marselha: Carta do Dia',                icon: '🃏', category: 'Tarot de Marselha' },
  { id: 'tarot-three',      name: 'Tarot de Marselha: Passado · Presente · Futuro', icon: '🃏', category: 'Tarot de Marselha' },
  { id: 'tarot-horseshoe',  name: 'Tarot de Marselha: Ferradura',                   icon: '🃏', category: 'Tarot de Marselha' },
  { id: 'tarot-celtic',     name: 'Tarot de Marselha: Cruz Celta',                  icon: '🃏', category: 'Tarot de Marselha' },
  // Tarot Cigano
  { id: 'lenormand-single', name: 'Tarot Cigano: Carta do Momento', icon: '🎴', category: 'Tarot Cigano' },
  { id: 'lenormand-three',  name: 'Tarot Cigano: Linha do Tempo',   icon: '🎴', category: 'Tarot Cigano' },
  { id: 'lenormand-five',   name: 'Tarot Cigano: Cruz',             icon: '🎴', category: 'Tarot Cigano' },
  { id: 'lenormand-nine',   name: 'Tarot Cigano: Quadrado',         icon: '🎴', category: 'Tarot Cigano' },
  // Sistemas
  { id: 'i-ching',          name: 'I Ching',                      icon: '☯️', category: 'Sistemas' },
  { id: 'numerology',       name: 'Numerologia',                  icon: '🔢', category: 'Sistemas' },
  { id: 'vedic-astrology',  name: 'Astrologia Védica',            icon: '✨', category: 'Sistemas' },
  { id: 'human-design',     name: 'Design Humano',                icon: '🧬', category: 'Sistemas' },
  // Eventos
  { id: 'agenda',           name: 'Agenda Astrológica',           icon: '📅', category: 'Eventos' },
  { id: 'moon-phases',      name: 'Fases da Lua',                 icon: '🌙', category: 'Eventos' },
  { id: 'retrogrado',       name: 'Radar Retrógrado',             icon: '↩️', category: 'Eventos' },
  { id: 'eclipses',         name: 'Eclipse Insight',              icon: '🌑', category: 'Eventos' },
  // Crescimento
  { id: 'astro-coach',      name: 'Astro Coach',                  icon: '🎓', category: 'Crescimento' },
  { id: 'rota-vocacional',  name: 'Rota Profissional: Mapa Vocacional',    icon: '🚀', category: 'Crescimento' },
  { id: 'rota-transition',  name: 'Rota Profissional: Transição de Carreira', icon: '🚀', category: 'Crescimento' },
  { id: 'astro-pet',        name: 'Astro Pet',                    icon: '🐾', category: 'Crescimento' },
  { id: 'dream-journal',    name: 'Diário dos Sonhos',            icon: '📔', category: 'Crescimento' },
];

const PLAN_TIERS: { id: PlanTier; label: string; color: string; bg: string }[] = [
  { id: 'free',    label: 'Gratuito', color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
  { id: 'monthly', label: 'Mensal',   color: '#4ade80', bg: 'rgba(74,222,128,0.1)'  },
  { id: 'annual',  label: 'Anual',    color: '#f59e0b', bg: 'rgba(245,158,11,0.1)'  },
];

const SETTINGS_KEY = 'plan_access_matrix';

const FREE_ENABLED_IDS = new Set([
  'sign-forecast', 'sign-synastry',
  'birth-chart',
  'forecast-daily',
  'tarot-single',
  'lenormand-single',
  'i-ching', 'numerology',
  'moon-phases',
]);

function defaultMatrix(): AccessMatrix {
  const matrix: AccessMatrix = {};
  for (const svc of SERVICES) {
    matrix[svc.id] = {
      free:    { enabled: FREE_ENABLED_IDS.has(svc.id), quota_per_month: null, quota_per_year: null },
      monthly: { enabled: true, quota_per_month: null, quota_per_year: null },
      annual:  { enabled: true, quota_per_month: null, quota_per_year: null },
    };
  }
  // [free_enabled, monthly_quota_month, annual_quota_month, annual_quota_year]
  type QuotaEntry = [boolean, number | null, number | null, number | null];
  const quotaOverrides: Record<string, QuotaEntry> = {
    'questions':        [false, 1,    3,    null],
    'synastry':         [false, 1,    3,    null],
    'astromatch':       [false, 1,    3,    null],
    'astrocartography': [false, 1,    2,    null],
    'forecast-annual':  [false, 1,    3,    1   ],
    'rota-vocacional':  [false, 1,    2,    1   ],
    'rota-transition':  [false, 1,    2,    1   ],
    'astro-pet':        [false, 1,    3,    null],
    'astro-coach':      [false, null, null, null],
  };
  for (const [id, [freeEnabled, mq, aq, aqy]] of Object.entries(quotaOverrides)) {
    if (matrix[id]) {
      matrix[id].free    = { enabled: freeEnabled, quota_per_month: null,  quota_per_year: null };
      matrix[id].monthly = { enabled: true,        quota_per_month: mq,    quota_per_year: null };
      matrix[id].annual  = { enabled: true,        quota_per_month: aq,    quota_per_year: aqy  };
    }
  }
  return matrix;
}

// ─────────────────────────────────────────
// Component
// ─────────────────────────────────────────

export function AdminPlanAccessPage() {
  const { showToast } = useToast();
  const [matrix,          setMatrix]          = useState<AccessMatrix>(defaultMatrix());
  const [comingSoon,      setComingSoon]      = useState<Set<string>>(new Set());
  const [testimonials,    setTestimonials]    = useState<Testimonial[]>([]);
  const [savingTest,      setSavingTest]      = useState(false);
  const [overrides,       setOverrides]       = useState<ServiceCatalogOverrides>({});
  const [editingSlug,     setEditingSlug]     = useState<string>(CATALOG_SERVICES[0]?.slug ?? '');
  const [savingOv,        setSavingOv]        = useState(false);
  const [avulsoProducts,  setAvulsoProducts]  = useState<AvulsoProduct[]>([]);
  const [savingAvulso,    setSavingAvulso]    = useState(false);
  const [avulsoMatrix,    setAvulsoMatrix]    = useState<Record<string, { enabled: boolean; price_brl: number; price_usd?: number }>>({});
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [filter,          setFilter]          = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const [matrixRes, testsRes, ovRes, avulsoRes, avulsoMxRes] = await Promise.all([
      supabase.from('platform_settings').select('value').eq('key', SETTINGS_KEY).maybeSingle(),
      supabase.from('platform_settings').select('value').eq('key', 'service_testimonials').maybeSingle(),
      supabase.from('platform_settings').select('value').eq('key', 'service_catalog_overrides').maybeSingle(),
      supabase.from('platform_settings').select('value').eq('key', 'avulso_products').maybeSingle(),
      supabase.from('platform_settings').select('value').eq('key', 'plan_access_avulso_pricing').maybeSingle(),
    ]);
    if (matrixRes.data?.value) {
      try {
        const parsed = typeof matrixRes.data.value === 'string' ? JSON.parse(matrixRes.data.value) : matrixRes.data.value;
        // Merge tier data with default to handle new services added since last save
        const merged = defaultMatrix();
        for (const svcId of Object.keys(parsed)) {
          if (svcId.startsWith('_')) continue; // skip _meta
          if (merged[svcId]) merged[svcId] = parsed[svcId];
        }
        setMatrix(merged);
        // Load coming-soon list from _meta
        const slugs = parsed._meta?.coming_soon_catalog_slugs;
        if (Array.isArray(slugs)) setComingSoon(new Set(slugs));
      } catch { /* use default */ }
    }
    if (testsRes.data?.value) {
      try {
        const parsed = typeof testsRes.data.value === 'string' ? JSON.parse(testsRes.data.value) : testsRes.data.value;
        if (Array.isArray(parsed)) setTestimonials(parsed as Testimonial[]);
      } catch { /* ignore */ }
    }
    if (ovRes.data?.value) {
      try {
        const parsed = typeof ovRes.data.value === 'string' ? JSON.parse(ovRes.data.value) : ovRes.data.value;
        if (parsed && typeof parsed === 'object') setOverrides(parsed as ServiceCatalogOverrides);
      } catch { /* ignore */ }
    }
    if (avulsoRes.data?.value) {
      try {
        const parsed = typeof avulsoRes.data.value === 'string' ? JSON.parse(avulsoRes.data.value) : avulsoRes.data.value;
        if (Array.isArray(parsed)) setAvulsoProducts(parsed as AvulsoProduct[]);
      } catch { /* ignore */ }
    }
    if (avulsoMxRes.data?.value) {
      try {
        const parsed = typeof avulsoMxRes.data.value === 'string' ? JSON.parse(avulsoMxRes.data.value) : avulsoMxRes.data.value;
        if (parsed && typeof parsed === 'object') setAvulsoMatrix(parsed as Record<string, { enabled: boolean; price_brl: number; price_usd?: number }>);
      } catch { /* ignore */ }
    }
    setLoading(false);
  }

  function toggleAvulso(svcId: string) {
    setAvulsoMatrix(prev => ({
      ...prev,
      [svcId]: { ...(prev[svcId] ?? { enabled: false, price_brl: 0 }), enabled: !(prev[svcId]?.enabled ?? false) },
    }));
  }

  async function saveAvulso() {
    setSavingAvulso(true);
    const { error } = await supabase
      .from('platform_settings')
      .upsert({
        key:         'avulso_products',
        value:       JSON.stringify(avulsoProducts),
        description: 'Produtos avulsos disponíveis para compra individual (fora dos planos)',
        updated_at:  new Date().toISOString(),
      }, { onConflict: 'key' });
    setSavingAvulso(false);
    if (error) {
      showToast({ type: 'error', message: 'Erro ao salvar produtos avulsos' });
    } else {
      showToast({ type: 'success', message: 'Produtos avulsos salvos!' });
    }
  }

  function addAvulso() {
    const id = `avulso_${Date.now()}`;
    setAvulsoProducts(prev => [...prev, { id, name: '', description: '', price_brl: 0, enabled: true }]);
  }

  function updateAvulso(idx: number, patch: Partial<AvulsoProduct>) {
    setAvulsoProducts(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  }

  function removeAvulso(idx: number) {
    setAvulsoProducts(prev => prev.filter((_, i) => i !== idx));
  }

  async function saveOverrides() {
    setSavingOv(true);
    // Strip empty strings so fallback kicks in for unset fields.
    const clean: ServiceCatalogOverrides = {};
    for (const [slug, ov] of Object.entries(overrides)) {
      const c: ServiceCatalogOverride = {};
      if (ov.name?.trim())        c.name        = ov.name.trim();
      if (ov.subtitle?.trim())    c.subtitle    = ov.subtitle.trim();
      if (ov.description?.trim()) c.description = ov.description.trim();
      if (ov.planNote?.trim())    c.planNote    = ov.planNote.trim();
      if (Object.keys(c).length > 0) clean[slug] = c;
    }
    const { error } = await supabase
      .from('platform_settings')
      .upsert({
        key:         'service_catalog_overrides',
        value:       JSON.stringify(clean),
        description: 'Overrides de nome/subtítulo/descrição/planNote por serviço',
        updated_at:  new Date().toISOString(),
      }, { onConflict: 'key' });
    setSavingOv(false);
    if (error) {
      showToast({ type: 'error', message: 'Erro ao salvar overrides' });
    } else {
      clearServiceCatalogOverridesCache();
      setOverrides(clean);
      showToast({ type: 'success', message: 'Overrides salvos!' });
    }
  }

  function updateOverride(slug: string, patch: Partial<ServiceCatalogOverride>) {
    setOverrides(prev => ({
      ...prev,
      [slug]: { ...(prev[slug] ?? {}), ...patch },
    }));
  }

  function clearOverride(slug: string) {
    setOverrides(prev => {
      const next = { ...prev };
      delete next[slug];
      return next;
    });
  }

  async function saveTestimonials() {
    setSavingTest(true);
    const { error } = await supabase
      .from('platform_settings')
      .upsert({
        key:         'service_testimonials',
        value:       JSON.stringify(testimonials),
        description: 'Depoimentos exibidos nas páginas /servicos/:slug',
        updated_at:  new Date().toISOString(),
      }, { onConflict: 'key' });
    setSavingTest(false);
    if (error) {
      showToast({ type: 'error', message: 'Erro ao salvar depoimentos' });
    } else {
      clearTestimonialsCache();
      showToast({ type: 'success', message: 'Depoimentos salvos!' });
    }
  }

  function addTestimonial() {
    setTestimonials(prev => [...prev, { name: '', city: '', text: '', slug: '' }]);
  }

  function updateTestimonial(idx: number, patch: Partial<Testimonial>) {
    setTestimonials(prev => prev.map((t, i) => i === idx ? { ...t, ...patch } : t));
  }

  function removeTestimonial(idx: number) {
    setTestimonials(prev => prev.filter((_, i) => i !== idx));
  }

  async function save() {
    setSaving(true);
    const payload = {
      ...matrix,
      _meta: {
        coming_soon_catalog_slugs: Array.from(comingSoon),
      },
    };
    const [r1, r2] = await Promise.all([
      supabase.from('platform_settings').upsert({
        key:         SETTINGS_KEY,
        value:       JSON.stringify(payload),
        description: 'Matriz de acesso por plano, configurado pelo admin',
        updated_at:  new Date().toISOString(),
      }, { onConflict: 'key' }),
      supabase.from('platform_settings').upsert({
        key:         'plan_access_avulso_pricing',
        value:       JSON.stringify(avulsoMatrix),
        description: 'Preços avulsos por serviço (one-off purchase)',
        updated_at:  new Date().toISOString(),
      }, { onConflict: 'key' }),
    ]);
    setSaving(false);
    if (r1.error || r2.error) {
      showToast({ type: 'error', message: 'Erro ao salvar configuração' });
    } else {
      clearPlanAccessMatrixCache();
      showToast({ type: 'success', message: 'Configuração salva com sucesso!' });
    }
  }

  function toggleComingSoon(slug: string) {
    setComingSoon(prev => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  }

  function toggleEnabled(svcId: string, tier: PlanTier) {
    setMatrix(prev => ({
      ...prev,
      [svcId]: {
        ...prev[svcId],
        [tier]: { ...prev[svcId][tier], enabled: !prev[svcId][tier].enabled },
      },
    }));
  }

  function setQuota(svcId: string, tier: PlanTier, val: string) {
    const quota = val === '' ? null : parseInt(val, 10);
    if (val !== '' && (isNaN(quota!) || quota! < 0)) return;
    setMatrix(prev => ({
      ...prev,
      [svcId]: {
        ...prev[svcId],
        [tier]: { ...prev[svcId][tier], quota_per_month: quota },
      },
    }));
  }

  function setQuotaYear(svcId: string, val: string) {
    const quota = val === '' ? null : parseInt(val, 10);
    if (val !== '' && (isNaN(quota!) || quota! < 0)) return;
    setMatrix(prev => ({
      ...prev,
      [svcId]: {
        ...prev[svcId],
        annual: { ...prev[svcId].annual, quota_per_year: quota },
      },
    }));
  }

  const filtered = filter.trim()
    ? SERVICES.filter(s => s.name.toLowerCase().includes(filter.toLowerCase()) || s.category.toLowerCase().includes(filter.toLowerCase()))
    : SERVICES;
  const filteredCategories = [...new Set(filtered.map(s => s.category))];

  return (
    <div>
      {/* Header */}
      <AdminPageHeader
        title="Plano de Acesso"
        objective="Controle quais serviços cada plano pode acessar e com qual limite mensal de uso. Cota = número máximo de usos por mês. 'Ilimitado' = sem restrição. CUIDADO: desativar um serviço para um plano afeta imediatamente todos os usuários desse plano, incluindo pagantes."
      />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <RefreshCw size={13} /> Recarregar
        </button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || loading}
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <Save size={13} /> {saving ? 'Salvando...' : 'Salvar configuração'}
        </button>
      </div>

      {/* Warning before matrix */}
      <div style={{ marginBottom: '1.25rem', padding: '0.625rem 1rem', backgroundColor: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius-md)' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: '#fca5a5', margin: 0 }}>
          ⚠️ Alterações na matriz de acesso têm efeito imediato. Verifique o impacto antes de salvar.
        </p>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {PLAN_TIERS.map(t => (
          <span key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: 'var(--text-caption)' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: t.color, display: 'inline-block' }} />
            <span style={{ color: t.color, fontWeight: 600 }}>
              {t.id === 'free' ? 'Gratuito (free)' : t.id === 'monthly' ? 'Mensal (monthly)' : 'Anual (annual)'}
            </span>
          </span>
        ))}
        <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
          · <span title="Cota: número máximo de usos por período. Anual tem dois campos: /mês e /ano (o mais restritivo prevalece). Vazio = ilimitado." style={{ borderBottom: '1px dashed var(--petrol)', cursor: 'help' }}>Cota</span>: nº de usos/mês ou /ano (vazio = ilimitado) · Toggle: ativa/desativa acesso
        </span>
      </div>

      {/* Search */}
      <input
        type="text"
        className="form-input"
        placeholder="Filtrar serviços..."
        value={filter}
        onChange={e => setFilter(e.target.value)}
        style={{ maxWidth: '300px', marginBottom: '1.25rem' }}
      />

      {loading ? (
        <div className="card shimmer" style={{ height: '400px' }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {filteredCategories.map(cat => (
            <div key={cat}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                {cat}
              </p>
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <th style={{ textAlign: 'left', padding: '0.6rem 1rem', fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', width: '35%' }}>
                        Serviço
                      </th>
                      {PLAN_TIERS.map(t => (
                        <th key={t.id} style={{ textAlign: 'center', padding: '0.6rem 0.5rem', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', color: t.color }}>
                          {t.id === 'free' ? 'Gratuito (free)' : t.id === 'monthly' ? 'Mensal (monthly)' : 'Anual (annual)'}
                        </th>
                      ))}
                      <th style={{ textAlign: 'center', padding: '0.6rem 0.5rem', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', color: '#c084fc' }}>
                        Avulso (one-off)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.filter(s => s.category === cat).map((svc, idx) => {
                      const row = matrix[svc.id];
                      if (!row) return null;
                      return (
                        <tr key={svc.id} style={{ borderBottom: idx < filtered.filter(s => s.category === cat).length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                          <td style={{ padding: '0.6rem 1rem' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>
                              <span>{svc.icon}</span>
                              <span>{svc.name}</span>
                            </span>
                          </td>
                          {PLAN_TIERS.map(t => {
                            const cell = row[t.id];
                            return (
                              <td key={t.id} style={{ padding: '0.4rem 0.5rem', textAlign: 'center', backgroundColor: cell.enabled ? t.bg : 'transparent' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem' }}>
                                  {/* Enable/disable toggle */}
                                  <button
                                    onClick={() => toggleEnabled(svc.id, t.id)}
                                    style={{
                                      width: '38px', height: '20px',
                                      borderRadius: '999px',
                                      border: 'none',
                                      cursor: 'pointer',
                                      backgroundColor: cell.enabled ? t.color : 'var(--bg-elevated)',
                                      position: 'relative',
                                      transition: 'background-color 0.15s',
                                      flexShrink: 0,
                                    }}
                                    title={cell.enabled ? 'Clique para desativar' : 'Clique para ativar'}
                                  >
                                    <span style={{
                                      position: 'absolute',
                                      top: '2px',
                                      left: cell.enabled ? '20px' : '2px',
                                      width: '16px', height: '16px',
                                      borderRadius: '50%',
                                      backgroundColor: '#fff',
                                      transition: 'left 0.15s',
                                    }} />
                                  </button>
                                  {/* Quota inputs, only when enabled */}
                                  {cell.enabled && (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                                        <input
                                          type="number"
                                          min={0}
                                          placeholder="∞"
                                          value={cell.quota_per_month ?? ''}
                                          onChange={e => setQuota(svc.id, t.id, e.target.value)}
                                          title="Cota mensal: nº máximo por mês. Deixe em branco para ilimitado."
                                          style={{
                                            width: '48px',
                                            textAlign: 'center',
                                            fontSize: '11px',
                                            padding: '2px 4px',
                                            border: '1px solid var(--border-subtle)',
                                            borderRadius: 'var(--radius-sm)',
                                            background: 'var(--bg-input)',
                                            color: 'var(--text-primary)',
                                          }}
                                        />
                                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>/mês</span>
                                      </div>
                                      {t.id === 'annual' && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                                          <input
                                            type="number"
                                            min={0}
                                            placeholder="∞"
                                            value={(cell as { quota_per_year?: number | null }).quota_per_year ?? ''}
                                            onChange={e => setQuotaYear(svc.id, e.target.value)}
                                            title="Cota anual: nº máximo por ano. Deixe em branco para ilimitado."
                                            style={{
                                              width: '48px',
                                              textAlign: 'center',
                                              fontSize: '11px',
                                              padding: '2px 4px',
                                              border: '1px solid rgba(245,158,11,0.5)',
                                              borderRadius: 'var(--radius-sm)',
                                              background: 'var(--bg-input)',
                                              color: '#f59e0b',
                                            }}
                                          />
                                          <span style={{ fontSize: '10px', color: '#f59e0b' }}>/ano</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                          {/* Avulso column */}
                          <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem' }}>
                              <button
                                onClick={() => toggleAvulso(svc.id)}
                                style={{ width: '38px', height: '20px', borderRadius: '999px', border: 'none', cursor: 'pointer',
                                  backgroundColor: avulsoMatrix[svc.id]?.enabled ? '#c084fc' : 'var(--bg-elevated)',
                                  position: 'relative', transition: 'background-color 0.15s', flexShrink: 0 }}
                                title={avulsoMatrix[svc.id]?.enabled ? 'Desativar venda avulsa' : 'Ativar venda avulsa'}
                              >
                                <span style={{ position: 'absolute', top: '2px', left: avulsoMatrix[svc.id]?.enabled ? '20px' : '2px',
                                  width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#fff', transition: 'left 0.15s' }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ────── "Em breve" section ────── */}
      {!loading && (
        <div style={{ marginTop: '2rem' }}>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
            <Clock size={13} style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
            Serviços em breve
          </p>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            Serviços marcados abaixo aparecem nas páginas /servicos/:slug com uma badge "Em breve" e o botão de acesso fica desabilitado para todos os usuários.
          </p>
          <div className="card" style={{ padding: '1rem' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '0.5rem',
            }}>
              {SERVICES.map(svc => {
                const checked = comingSoon.has(svc.id);
                return (
                  <label
                    key={svc.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border-subtle)',
                      background: checked ? 'rgba(245,200,66,0.1)' : 'transparent',
                      cursor: 'pointer',
                      fontSize: 'var(--text-label)',
                      color: checked ? 'var(--petrol)' : 'var(--text-secondary)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleComingSoon(svc.id)}
                      style={{ accentColor: 'var(--petrol)' }}
                    />
                    <span>{svc.icon}</span>
                    <span style={{ flex: 1 }}>{svc.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ────── Testimonials editor ────── */}
      {!loading && (
        <div style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
              <MessageSquareQuote size={13} style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
              Depoimentos de usuárias
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={addTestimonial} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Plus size={13} /> Adicionar
              </button>
              <button className="btn btn-primary btn-sm" onClick={saveTestimonials} disabled={savingTest} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Save size={13} /> {savingTest ? 'Salvando...' : 'Salvar depoimentos'}
              </button>
            </div>
          </div>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            Deixe "Slug" em branco para exibir o depoimento em TODAS as páginas. Preencha com um slug (ex: sinastria) para exibir só naquele serviço. Até 3 depoimentos são mostrados por página.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {testimonials.length === 0 && (
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Nenhum depoimento cadastrado. A seção fica oculta nas landing pages enquanto a lista estiver vazia.
              </p>
            )}
            {testimonials.map((t, i) => (
              <div key={i} className="card" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem' }}>
                  <input
                    className="form-input"
                    placeholder="Nome"
                    value={t.name}
                    onChange={e => updateTestimonial(i, { name: e.target.value })}
                  />
                  <input
                    className="form-input"
                    placeholder="Cidade"
                    value={t.city}
                    onChange={e => updateTestimonial(i, { city: e.target.value })}
                  />
                  <select
                    className="form-input"
                    value={t.slug ?? ''}
                    onChange={e => updateTestimonial(i, { slug: e.target.value || undefined })}
                  >
                    <option value="">Todas as páginas (sem filtro)</option>
                    {CATALOG_SERVICES.map(svc => (
                      <option key={svc.slug} value={svc.slug}>{svc.icon} {svc.name}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  className="form-input"
                  placeholder="Depoimento (frase em 1ª pessoa)"
                  rows={2}
                  value={t.text}
                  onChange={e => updateTestimonial(i, { text: e.target.value })}
                />
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => removeTestimonial(i)}
                  style={{ alignSelf: 'flex-start', color: '#f87171', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                >
                  <Trash2 size={13} /> Remover
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ────── Catalog overrides editor ────── */}
      {!loading && (() => {
        const current = overrides[editingSlug] ?? {};
        const svc     = CATALOG_SERVICES.find(s => s.slug === editingSlug);
        return (
          <div style={{ marginTop: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                <FileEdit size={13} style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
                Overrides de conteúdo (nome, subtítulo, descrição)
              </p>
              <button className="btn btn-primary btn-sm" onClick={saveOverrides} disabled={savingOv} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Save size={13} /> {savingOv ? 'Salvando...' : 'Salvar overrides'}
              </button>
            </div>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              Reescreva os campos curtos de qualquer um dos 21 serviços sem redeployar. Campos em branco preservam o conteúdo original do catálogo.
            </p>

            <div className="card" style={{ padding: '1rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Serviço
                </span>
                <select
                  className="form-input"
                  value={editingSlug}
                  onChange={e => setEditingSlug(e.target.value)}
                >
                  {CATALOG_SERVICES.map(s => (
                    <option key={s.slug} value={s.slug}>
                      {s.icon} {s.name} ({s.slug})
                    </option>
                  ))}
                </select>
              </label>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600 }}>
                    Nome (padrão: <em>{svc?.name}</em>)
                  </span>
                  <input
                    className="form-input"
                    placeholder={svc?.name}
                    value={current.name ?? ''}
                    onChange={e => updateOverride(editingSlug, { name: e.target.value })}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600 }}>
                    Subtítulo (padrão: <em>{svc?.subtitle}</em>)
                  </span>
                  <input
                    className="form-input"
                    placeholder={svc?.subtitle}
                    value={current.subtitle ?? ''}
                    onChange={e => updateOverride(editingSlug, { subtitle: e.target.value })}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600 }}>
                    Descrição (parágrafo do Hero)
                  </span>
                  <textarea
                    className="form-input"
                    rows={4}
                    placeholder={svc?.description}
                    value={current.description ?? ''}
                    onChange={e => updateOverride(editingSlug, { description: e.target.value })}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 600 }}>
                    Plan note, badge abaixo do CTA (padrão: <em>{svc?.planNote ?? '-'}</em>)
                  </span>
                  <input
                    className="form-input"
                    placeholder={svc?.planNote ?? ''}
                    value={current.planNote ?? ''}
                    onChange={e => updateOverride(editingSlug, { planNote: e.target.value })}
                  />
                </label>

                {overrides[editingSlug] && Object.keys(overrides[editingSlug]).length > 0 && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => clearOverride(editingSlug)}
                    style={{ alignSelf: 'flex-start', color: '#f87171', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                  >
                    <Trash2 size={13} /> Limpar overrides deste serviço
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ────── Avulso products editor ────── */}
      {!loading && (
        <div style={{ marginTop: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
              <ShoppingCart size={13} style={{ verticalAlign: 'middle', marginRight: '0.35rem' }} />
              Produtos Avulsos
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={addAvulso} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Plus size={13} /> Adicionar
              </button>
              <button className="btn btn-primary btn-sm" onClick={saveAvulso} disabled={savingAvulso} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                <Save size={13} /> {savingAvulso ? 'Salvando...' : 'Salvar avulsos'}
              </button>
            </div>
          </div>
          <div style={{ marginBottom: '0.75rem', padding: '0.75rem 1rem', backgroundColor: 'rgba(31,78,95,0.06)', border: '1px solid var(--border-gold)', borderRadius: 'var(--radius-md)' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', margin: 0 }}>
              ℹ️ Os produtos avulsos marcados como ativos aparecem automaticamente na página de preços (/pricing) e nas páginas de serviço vinculadas. Configure o preço em BRL e USD para suportar múltiplas moedas.
            </p>
          </div>
          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            Serviços que <strong>não estão incluídos</strong> em nenhum plano e podem ser adquiridos individualmente. Vincule ao slug de uma página de serviço para exibir o botão de compra comparativa.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {avulsoProducts.length === 0 && (
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Nenhum produto avulso cadastrado.
              </p>
            )}
            {avulsoProducts.map((prod, i) => (
              <div key={prod.id} className="card" style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', border: prod.enabled ? '1px solid rgba(31,78,95,0.3)' : '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.5rem' }}>
                  <input
                    className="form-input"
                    placeholder="Nome do produto"
                    value={prod.name}
                    onChange={e => updateAvulso(i, { name: e.target.value })}
                  />
                  <input
                    className="form-input"
                    placeholder="Preço (R$)"
                    type="number"
                    min={0}
                    step={0.01}
                    value={prod.price_brl || ''}
                    onChange={e => updateAvulso(i, { price_brl: parseFloat(e.target.value) || 0 })}
                  />
                  <input
                    className="form-input"
                    placeholder="Preço (USD)"
                    type="number"
                    min={0}
                    step={0.01}
                    value={prod.price_usd || ''}
                    onChange={e => updateAvulso(i, { price_usd: parseFloat(e.target.value) || 0 })}
                  />
                  <select
                    className="form-input"
                    value={prod.service_slug ?? ''}
                    onChange={e => updateAvulso(i, { service_slug: e.target.value || undefined })}
                  >
                    <option value="">Sem página de serviço</option>
                    {CATALOG_SERVICES.map(svc => (
                      <option key={svc.slug} value={svc.slug}>{svc.icon} {svc.name}</option>
                    ))}
                  </select>
                </div>
                {prod.service_slug && (
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
                    <strong style={{ color: 'var(--text-secondary)' }}>Página de destino:</strong>{' '}
                    <code style={{ fontSize: '11px', color: 'var(--petrol)', background: 'var(--bg-base)', padding: '1px 5px', borderRadius: '3px' }}>
                      /servicos/{prod.service_slug}
                    </code>
                  </p>
                )}
                <input
                  className="form-input"
                  placeholder="Descrição curta (opcional)"
                  value={prod.description ?? ''}
                  onChange={e => updateAvulso(i, { description: e.target.value })}
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-label)', color: prod.enabled ? 'var(--petrol)' : 'var(--text-muted)' }}>
                    <input
                      type="checkbox"
                      checked={prod.enabled}
                      onChange={e => updateAvulso(i, { enabled: e.target.checked })}
                      style={{ accentColor: 'var(--petrol)' }}
                    />
                    {prod.enabled ? 'Ativo (visível para compra)' : 'Inativo'}
                  </label>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => removeAvulso(i)}
                    style={{ color: '#f87171', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                  >
                    <Trash2 size={13} /> Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save notice */}
      <div style={{ marginTop: '1.5rem', padding: '0.75rem 1rem', backgroundColor: 'rgba(245,200,66,0.08)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(245,200,66,0.2)' }}>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', margin: 0 }}>
          ⚠️ <strong>Nota:</strong> Esta configuração é salva como referência administrativa. A aplicação das regras nas edge functions é independente, use este painel para documentar e coordenar as políticas de acesso.
        </p>
      </div>
    </div>
  );
}
